/**
 * 包装单位管理前端接口
 * @author ChaseZhang
 * @since 2025-01-27
 */

import { httpClient as request } from '@/utils/http'

// ==================== 类型定义 ====================

/**
 * 包装单位响应模型
 */
export interface PackageUnitModel {
  /** 唯一标识 */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 2位包装代码 */
  code2Digit: string;
  /** 4位包装代码 */
  code4Digit?: string;
  /** 中文名称 */
  nameCn: string;
  /** 英文名称 */
  nameEn?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled: number;
  /** 是否为平台数据 */
  isPlatformData?: boolean;
  /** EDI代码列表 */
  ediCodes?: CommonEdiCodeModel[];
}

/**
 * 通用EDI代码模型
 */
export interface CommonEdiCodeModel {
  /** EDI代码ID */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
  /** EDI类型: 1-承运人, 2-包装单位, 3-集装箱类型 */
  ediType: number;
  /** 业务实体代码 */
  businessEntityCode?: string;
  /** 业务实体ID */
  businessEntityId?: string;
  /** 承运人ID（可选） */
  carrierId?: string;
  /** 启用状态 */
  enabled: number;
  /** 创建时间 */
  dateCreated: string;
  /** 修改时间 */
  dateModified: string;
}

/**
 * 包装单位查询请求模型
 */
export interface PackageUnitQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 2位包装代码，支持模糊查询 */
  code2Digit?: string;
  /** 4位包装代码，支持模糊查询 */
  code4Digit?: string;
  /** 中文名称，支持模糊查询 */
  nameCn?: string;
  /** 英文名称，支持模糊查询 */
  nameEn?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled?: number;
  /** 租户ID */
  tenantId?: string;
}

/**
 * 包装单位保存请求模型
 */
export interface PackageUnitSaveRequest {
  /** 主键ID，更新时传递 */
  id?: string;
  /** 2位包装代码 */
  code2Digit: string;
  /** 4位包装代码 */
  code4Digit?: string;
  /** 中文名称 */
  nameCn: string;
  /** 英文名称 */
  nameEn?: string;
  /** 启用状态: 1-启用, 0-禁用，默认为1 */
  enabled?: number;
  /** 租户ID */
  tenantId?: string;
}

/**
 * 模糊搜索请求参数
 */
export interface FuzzySearchRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 搜索关键字，支持模糊匹配名称和代码 */
  keyword?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled?: number;
  /** 租户ID */
  tenantId?: string;
}

/**
 * EDI代码信息
 */
export interface EdiCodeInfo {
  /** 平台名称 */
  platform: string;
  /** 承运人ID（可选） */
  carrier?: string;
  /** EDI代码 */
  ediCode: string;
}

/**
 * 包装单位EDI保存请求
 */
export interface PackageUnitEdiSaveRequest {
  /** 包装代码 */
  code: string;
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
}

/**
 * 批量包装单位EDI保存请求
 */
export interface BatchPackageUnitEdiSaveRequest {
  /** 包装代码 */
  code: string;
  /** EDI代码列表 */
  ediCodes: EdiCodeInfo[];
}

/**
 * 包装单位EDI查询请求
 */
export interface PackageUnitEdiQueryRequest {
  /** 包装代码 */
  code: string;
}

/**
 * 包装单位EDI检查请求
 */
export interface PackageUnitEdiCheckRequest {
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
}

/**
 * 分页响应模型
 */
export interface PageResponse<T> {
  /** 当前页码 */
  current: number;
  /** 每页大小 */
  size: number;
  /** 总记录数 */
  total: number;
  /** 总页数 */
  pages: number;
  /** 数据列表 */
  records: T[];
}

/**
 * 查询平台数据请求
 */
export interface QueryPlatformRequest {
  /** 页码 */
  pageNum?: number;
  /** 页大小 */
  pageSize?: number;
  /** 搜索关键字 */
  keyword?: string;
  /** 租户ID */
  tenantId?: string;
}

/**
 * 启用状态枚举
 */
export enum EnabledStatus {
  /** 禁用 */
  DISABLED = 0,
  /** 启用 */
  ENABLED = 1
}

// ==================== API接口 ====================

/**
 * 包装单位管理API类
 */
export class PackageUnitApi {
  private static readonly BASE_URL = '/mdm/packageUnit'

  // ==================== 核心CRUD接口 ====================

  /**
   * 分页查询包装单位列表
   */
  static async queryPage(params: PackageUnitQueryRequest): Promise<PageResponse<PackageUnitModel>> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryPage`, params)
  }

  /**
   * 查询包装单位列表（不分页）
   */
  static async queryList(params: PackageUnitQueryRequest): Promise<PackageUnitModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryList`, params)
  }

  /**
   * 模糊搜索包装单位
   */
  static async search(params: FuzzySearchRequest): Promise<PackageUnitModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/search`, params)
  }

  /**
   * 根据ID获取包装单位详情
   */
  static async getById(id: string): Promise<PackageUnitModel> {
    return request.post(`${PackageUnitApi.BASE_URL}/getById`, { id })
  }

  /**
   * 保存包装单位（新增或更新）
   */
  static async save(params: PackageUnitSaveRequest): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/save`, params)
  }

  /**
   * 删除包装单位
   */
  static async delete(id: string): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/delete`, { id })
  }

  /**
   * 更新包装单位状态
   */
  static async updateStatus(id: string, enabled: number): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/updateStatus`, { id, enabled })
  }

  // ==================== 批量操作接口 ====================

  /**
   * 批量保存包装单位
   */
  static async batchSave(packageUnits: PackageUnitSaveRequest[]): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/batchSave`, { packageUnits })
  }

  /**
   * 批量删除包装单位
   */
  static async batchDelete(ids: string[]): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/batchDelete`, { ids })
  }

  /**
   * 批量更新包装单位状态
   */
  static async batchUpdateStatus(ids: string[], enabled: number): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/batchUpdateStatus`, {
      ids,
      enabled
    })
  }

  // ==================== 专用查询接口 ====================

  /**
   * 根据包装代码查询包装单位
   */
  static async getByCode(code: string): Promise<PackageUnitModel> {
    return request.post(`${PackageUnitApi.BASE_URL}/getByCode`, { code })
  }

  /**
   * 根据包装代码批量查询
   */
  static async queryByCodes(codes: string[]): Promise<PackageUnitModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryByCodes`, { codes })
  }

  /**
   * 查询启用状态的包装单位
   */
  static async queryEnabled(): Promise<PackageUnitModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryEnabled`, {})
  }

  /**
   * 查询常用包装单位
   */
  static async queryCommon(): Promise<PackageUnitModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryCommon`, {})
  }

  // ==================== 租户与平台关联接口 ====================

  /**
   * 分页查询可关联的平台包装单位
   */
  static async queryPlatformPage(params: QueryPlatformRequest): Promise<PageResponse<PackageUnitModel>> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryPlatformPage`, params)
  }

  /**
   * 查询可关联的平台包装单位列表
   */
  static async queryPlatformList(params: QueryPlatformRequest): Promise<PackageUnitModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/queryPlatformList`, params)
  }

  /**
   * 批量关联平台包装单位到租户
   */
  static async batchAssociate(platformIds: string[]): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/batchAssociate`, {
      platformIds
    })
  }

  /**
   * 批量解除关联平台包装单位
   */
  static async batchDisassociate(platformIds: string[]): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/batchDisassociate`, {
      platformIds
    })
  }

  // ==================== EDI代码管理接口 ====================

  /**
   * 查询包装单位的EDI代码列表
   */
  static async queryEdiCodes(params: PackageUnitEdiQueryRequest): Promise<CommonEdiCodeModel[]> {
    return request.post(`${PackageUnitApi.BASE_URL}/edi/queryList`, params)
  }

  /**
   * 为包装单位添加EDI代码
   */
  static async saveEdiCode(params: PackageUnitEdiSaveRequest): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/edi/save`, params)
  }

  /**
   * 批量为包装单位添加EDI代码
   */
  static async batchSaveEdiCodes(params: BatchPackageUnitEdiSaveRequest): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/edi/batchSave`, params)
  }

  /**
   * 删除包装单位的EDI代码
   */
  static async deleteEdiCode(ediCodeId: string): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/edi/delete`, { ediCodeId })
  }

  /**
   * 批量删除包装单位的EDI代码
   */
  static async batchDeleteEdiCodes(ediCodeIds: string[]): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/edi/batchDelete`, {
      ediCodeIds
    })
  }

  /**
   * 检查包装单位EDI代码是否存在
   */
  static async checkEdiCodeExists(params: PackageUnitEdiCheckRequest): Promise<boolean> {
    return request.post(`${PackageUnitApi.BASE_URL}/edi/checkExists`, params)
  }
}

// ==================== 便捷服务 ====================

/**
 * 包装单位管理便捷服务
 */
export const PackageUnitService = {
  // 核心CRUD
  queryPage: PackageUnitApi.queryPage,
  queryList: PackageUnitApi.queryList,
  search: PackageUnitApi.search,
  getById: PackageUnitApi.getById,
  save: PackageUnitApi.save,
  delete: PackageUnitApi.delete,
  updateStatus: PackageUnitApi.updateStatus,

  // 批量操作
  batchSave: PackageUnitApi.batchSave,
  batchDelete: PackageUnitApi.batchDelete,
  batchUpdateStatus: PackageUnitApi.batchUpdateStatus,

  // 专用查询
  getByCode: PackageUnitApi.getByCode,
  queryByCodes: PackageUnitApi.queryByCodes,
  queryEnabled: PackageUnitApi.queryEnabled,
  queryCommon: PackageUnitApi.queryCommon,

  // 租户关联
  queryPlatformPage: PackageUnitApi.queryPlatformPage,
  queryPlatformList: PackageUnitApi.queryPlatformList,
  batchAssociate: PackageUnitApi.batchAssociate,
  batchDisassociate: PackageUnitApi.batchDisassociate,

  // EDI代码管理
  queryEdiCodes: PackageUnitApi.queryEdiCodes,
  saveEdiCode: PackageUnitApi.saveEdiCode,
  batchSaveEdiCodes: PackageUnitApi.batchSaveEdiCodes,
  deleteEdiCode: PackageUnitApi.deleteEdiCode,
  batchDeleteEdiCodes: PackageUnitApi.batchDeleteEdiCodes,
  checkEdiCodeExists: PackageUnitApi.checkEdiCodeExists
}

export default PackageUnitApi
